import { ref } from 'vue';

import { defineStore } from 'pinia';

export const useAlertsStore = defineStore('alertsStore', () => {
  const errors = ref([]);

  const setErrors = (error, defaultError = true) => {
    if (!error) return;

    // Silent ignore — these are internal plumbing signals, not user-actionable errors.
    // Axios/fetch cancellations (user navigated, dedup, abort controller fired).
    if (error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.name === 'CanceledError') return;
    // Internal abort from $axios.setAuthorizationHeader while an auth transition is running.
    if (error.code === 'ERR_AUTH_LOADING') return;
    // Forced logout after MAX_REFRESH_FAILURES consecutive 401s (tokenService.handleRefreshFailure).
    // The session is already being torn down — a technical "Logging out." toast is just noise.
    if (error.code === 'ERR_AUTH_LOGOUT') return;
    // No app-token session to mint a per-BP token (tokenService.#ensureAppToken). This is the
    // session-expiry/logout transition — the app is already redirecting to login, just noise.
    if (error.code === 'ERR_NO_SESSION') return;
    // 401 from background/authenticated requests is token-expiry noise the interceptor handles
    // (refresh → retry → force-logout). A 401 the interceptor flagged as user-facing — login,
    // register, confirm, forgot/reset — is a real error and must surface.
    if (error?.response?.status === 401 && !error.isUserFacingAuthError) return;
    // /auth/refresh-token failures are internal: the user never asked for a refresh, and
    // the interceptor decides whether to retry or log out. Backend messages like
    // "Invalid refresh token" must not leak to the toast.
    const url = error?.config?.url || '';
    if (url.includes('/auth/refresh-token') || url.includes('/auth/refreshToken')) return;
    // Legacy body-code 401 filter (kept until we confirm no API still uses it).
    if (error?.response?.data?.code === 401) return;

    errors.value = defaultError
      ? (error?.response?.data?.details?.[0] ??
        error?.response?.data?.detail?.[0]?.msg ??
        (typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : undefined) ??
        error?.response?.data?.message ??
        error?.message ??
        'Something went wrong')
      : error;
  };
  const clearErrors = (error, defaultError = true) => {
    errors.value = '';
  };

  return {
    errors,
    setErrors,
    clearErrors,
  };
});
